import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { useGameStore } from '../../core/store';
import {
  preloadAudio,
  unlockAudio,
} from '../audio';
import './tunnel-ui.css';

export function CharacterCreateUI() {
  const root = useRef<HTMLDivElement>(null);
  const profile = useGameStore((s) => s.profile);
  const tunnelReady = useGameStore((s) => s.tunnelReady);
  const setTunnelReady = useGameStore((s) => s.setTunnelReady);
  const setName = useGameStore((s) => s.setName);
  const setNumber = useGameStore((s) => s.setNumber);
  const setFoot = useGameStore((s) => s.setFoot);
  const startTunnelWalk = useGameStore((s) => s.startTunnelWalk);
  const setOverlayOpacity = useGameStore((s) => s.setOverlayOpacity);
  const tunnelWalking = useGameStore((s) => s.tunnelWalking);
  const [line, setLine] = useState(0);

  useEffect(() => {
    void unlockAudio().then(() => preloadAudio());
  }, []);

  // ACT1 intro copy beat
  useEffect(() => {
    if (tunnelReady) return;
    const timers = [
      window.setTimeout(() => setLine(1), 1600),
      window.setTimeout(() => setLine(2), 3200),
      window.setTimeout(() => setTunnelReady(true), 5200),
    ];
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [tunnelReady, setTunnelReady]);

  useEffect(() => {
    if (!tunnelReady || !root.current) return;
    gsap.fromTo(
      root.current,
      { opacity: 0, y: 24 },
      { opacity: 1, y: 0, duration: 0.9, ease: 'power3.out' },
    );
  }, [tunnelReady]);

  const onConfirm = () => {
    const el = root.current;
    const tl = gsap.timeline();
    if (el) {
      tl.to(el, { opacity: 0, y: -12, duration: 0.45, ease: 'power2.in' }, 0);
    }
    tl.add(() => {
      startTunnelWalk();
      setOverlayOpacity(0);
    });
  };

  if (tunnelWalking) return null;

  if (!tunnelReady) {
    return (
      <div className="tunnel-intro" aria-live="polite">
        <p className={`tunnel-intro__line${line >= 0 ? ' is-on' : ''}`}>
          This isn&apos;t a football story.
        </p>
        <p className={`tunnel-intro__line tunnel-intro__line--pause${line >= 1 ? ' is-on' : ''}`}>
          It&apos;s an engineering journey.
        </p>
        <p className={`tunnel-intro__hint${line >= 2 ? ' is-on' : ''}`}>
          Every sprint is learning. Every skill is a technology mastered.
        </p>
        <button
          type="button"
          className="tunnel-intro__skip"
          onClick={() => setTunnelReady(true)}
        >
          Skip
        </button>
      </div>
    );
  }

  return (
    <div className="tunnel-ui" ref={root}>
      <div className="tunnel-ui__announce">
        <p className="tunnel-ui__eyebrow">Identity</p>
        <h1 className="tunnel-ui__title">Create your journey identity.</h1>
        <p className="tunnel-ui__sub">
          This isn&apos;t a match. It&apos;s your walk through an engineering
          career — toward the light at the end of the tunnel.
        </p>
      </div>

      <form
        className="tunnel-ui__form"
        onSubmit={(e) => {
          e.preventDefault();
          onConfirm();
        }}
      >
        <label className="field">
          <span>Name</span>
          <input
            value={profile.name}
            maxLength={12}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => setName(e.target.value)}
            placeholder="YOUR NAME"
          />
        </label>

        <label className="field">
          <span>Jersey Number</span>
          <input
            type="number"
            min={1}
            max={99}
            value={profile.number}
            onChange={(e) => setNumber(Number(e.target.value))}
          />
        </label>

        <fieldset className="field">
          <legend>Preferred Foot</legend>
          <div className="seg">
            {(['left', 'right'] as const).map((foot) => (
              <button
                key={foot}
                type="button"
                className={profile.foot === foot ? 'is-active' : ''}
                onClick={() => setFoot(foot)}
              >
                {foot}
              </button>
            ))}
          </div>
        </fieldset>

        <button type="button" className="tunnel-ui__cta" onClick={onConfirm}>
          Begin the Journey
        </button>
      </form>
    </div>
  );
}
